import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, FileDown, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import camApi from "@/lib/camApi";

const HEADERS = [
  "Name", "Category", "Sub-category", "Asset tag", "Brand", "Model", "Serial number",
  "Condition", "Status", "Purchase date", "Vendor", "Purchase cost", "Warranty expiry",
  "Room", "Lab", "Department", "Assigned email",
];
const EXAMPLES = [
  ["Dell OptiPlex 7010", "Desktop", "", "", "Dell", "OptiPlex 7010", "SN12345", "good", "in_store", "2024-06-01", "Dell India", "48000", "2027-06-01", "", "Computer Lab 2", "CSE", ""],
  ["Cisco SG350 Switch", "Network Devices", "", "", "Cisco", "SG350-28", "FOC123", "good", "installed", "2023-02-10", "Ingram", "35000", "2026-02-10", "Server Room", "", "IT", ""],
];
const CHUNK = 50;

export default function BulkImportDialog({ onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...EXAMPLES]);
    ws["!cols"] = HEADERS.map(() => ({ wch: 16 }));
    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["How to use this template"],
      [],
      ["• Name and Category are required. Category / Sub-category / Room / Lab / Department must match names already in CAM."],
      ["• Condition: new, good, fair, poor, damaged. Status: in_store, installed, assigned, in_repair, in_maintenance, transferred, disposed."],
      ["• Leave Asset tag blank to auto-generate. Dates as YYYY-MM-DD."],
      ["• Any extra column whose header matches a category's spec field (e.g. RAM (GB)) is imported as that field."],
      ["• Assigned email assigns the asset to that user (must exist in your institute)."],
    ]), "Instructions");
    XLSX.writeFile(wb, "CAM-asset-import-template.xlsx");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const cleaned = json.filter((r) => Object.values(r).some((v) => String(v).trim() !== "") && String(r.Name || r.name || "").trim() !== "");
      if (!cleaned.length) { toast.error("No rows with a Name found in the first sheet."); setRows(null); return; }
      setRows(cleaned);
    } catch (err) {
      toast.error("Couldn't read that file. Use the .xlsx/.csv template."); setRows(null);
    } finally { if (inputRef.current) inputRef.current.value = ""; }
  };

  const run = async () => {
    if (!rows?.length) return;
    setBusy(true); setProgress(0);
    const agg = { created: 0, failed: 0, errors: [] };
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const batch = rows.slice(i, i + CHUNK);
        const r = await camApi.bulkImport(batch, i);
        agg.created += r.created || 0;
        agg.failed += r.failed || 0;
        if (r.errors?.length) agg.errors.push(...r.errors);
        setProgress(Math.min(i + CHUNK, rows.length));
      }
      setResult(agg);
      if (agg.created) toast.success(`${agg.created} asset${agg.created === 1 ? "" : "s"} imported`);
      if (agg.created && !agg.failed) { onDone?.(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto touch-scroll">
        <DialogHeader><DialogTitle className="font-display tracking-tighter flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-cobalt-500" />Import assets</DialogTitle></DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="rounded-xl border bg-black/[0.02] dark:bg-white/[0.03] p-3 flex items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground">New here? Start from the template — it has the exact columns and examples.</div>
              <Button size="sm" variant="outline" className="shrink-0" onClick={downloadTemplate}><FileDown className="h-4 w-4 mr-1.5" />Template</Button>
            </div>

            <label className="block rounded-xl border-2 border-dashed border-cobalt-500/40 hover:border-cobalt-500 hover:bg-cobalt-500/[0.03] transition-colors p-8 text-center cursor-pointer">
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} />
              <Upload className="h-7 w-7 mx-auto text-cobalt-500" />
              <div className="mt-2 text-sm font-medium">{fileName || "Choose a .xlsx or .csv file"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{rows ? `${rows.length} row${rows.length === 1 ? "" : "s"} ready` : "Categories & locations are matched by name"}</div>
            </label>

            {busy && (
              <div>
                <div className="h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden"><div className="h-full bg-cobalt-500 transition-all" style={{ width: `${rows ? (progress / rows.length) * 100 : 0}%` }} /></div>
                <div className="text-xs text-muted-foreground mt-1 text-center">Importing {progress} / {rows?.length}…</div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={run} disabled={!rows?.length || busy}>{busy ? "Importing…" : `Import ${rows?.length || 0} rows`}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="glass rounded-xl p-4 text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto text-green-500" />
                <div className="metric text-2xl font-display mt-1">{result.created}</div>
                <div className="text-xs text-muted-foreground">imported</div>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <AlertTriangle className={`h-6 w-6 mx-auto ${result.failed ? "text-amber-500" : "text-muted-foreground/40"}`} />
                <div className="metric text-2xl font-display mt-1">{result.failed}</div>
                <div className="text-xs text-muted-foreground">skipped</div>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div className="rounded-xl border max-h-56 overflow-y-auto touch-scroll">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b sticky top-0 bg-background">Rows that need attention</div>
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-2 text-sm border-b last:border-0 flex gap-2">
                    <span className="text-mono text-xs text-muted-foreground shrink-0">Row {e.row}</span>
                    <span className="min-w-0"><span className="font-medium">{e.name || "—"}</span> — <span className="text-red-500">{e.error}</span></span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setResult(null); setRows(null); setFileName(""); }}>Import more</Button>
              <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={() => { onDone?.(); onClose(); }}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
